import axios from 'axios';
// IMPORTANT: import the loader from `@stripe/stripe-js/pure`, NOT `@stripe/stripe-js`.
// The main entry has a module-eval side effect (`Promise.resolve().then(getStripePromise)`)
// that injects the `js.stripe.com` <script> the instant the module is imported — so any
// static import in the boot bundle would fetch Stripe.js on EVERY page, regardless of
// whether `loadStripe()` is ever called. The `/pure` variant defers injection to the
// first real `loadStripe()` call, which is the whole point of loading it lazily here.
import { loadStripe } from '@stripe/stripe-js/pure';
import type { Stripe } from '@stripe/stripe-js';

import store from '@/store';
import {
  sessionInvoiceMatchesConfig,
  type SessionInvoice,
  type SessionInvoiceConfig,
} from './session-invoice';

export { sessionInvoiceMatchesConfig };
export type { SessionInvoice, SessionInvoiceConfig };

/**
 * Shared Stripe wiring for every checkout in the app — the config-driven Spark
 * module (`src/components/modules/spark/stripe-payment`), the nova configurator-v3
 * checkout (`src/nova/pages/configurator-v3`), and the nova product-detail payment
 * modal (`src/nova/pages/product-detail`). Keeping it here means ONE Stripe.js
 * instance app-wide and one definition of the loader/invoice contract.
 *
 * Stripe Elements *appearance* deliberately stays with each consumer — it is
 * theme-/design-specific (configurator chrome vs modal vs legacy Spark) and the
 * config area cannot read nova's theme tokens.
 */

/** True once a Stripe publishable key is configured on the host page. */
export function isStripeLive(): boolean {
  return Boolean(window.config?.stripePublicKey);
}

// Stripe's UI language lives in `./stripe-locale` (kept store-free so it stays unit-testable).
// Every `<Elements>` in the app must pass it: Stripe defaults to the BROWSER's language, not
// ours. Deliberately a plain comment, not JSDoc — it documents a neighbouring module, and as
// JSDoc it would attach itself to `stripePromise` below.

let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Lazily load Stripe.js once, app-wide. Resolves to `null` when no publishable
 * key is configured (demo mode). Loading is deferred to the first call — never at
 * module-eval time — so Stripe.js is not fetched on pages that never reach a
 * payment flow.
 */
export function getStripe(): Promise<Stripe | null> {
  if (!isStripeLive()) return Promise.resolve(null);
  if (!stripePromise) stripePromise = loadStripe(window.config.stripePublicKey);
  return stripePromise;
}

/**
 * Create (or, since the endpoint is IDEMPOTENT per session, re-fetch the existing pending) invoice
 * for a configurator session and return the FULL owner-invoice row.
 *
 * `POST ${sessionApiUrl}/invoice { session_id }` (owner's Bearer attached). The backend derives the
 * amount + line items from the SAVED session and returns the invoice, with the Stripe PaymentIntent
 * clientSecret as `provider_reference`. The schema accepts ONLY `session_id` (probed: it rejects
 * `email_address`/`email` in the body and ignores them in the query).
 *
 * IDEMPOTENCY: while a `pending` invoice exists for the session, every call returns THAT SAME
 * invoice — same id, price, line items and clientSecret — even after the session's selection has
 * changed. Detecting + reissuing a stale one is `reconcileSessionInvoice` below.
 *
 * KNOWN BACKEND GAP: this requires the SESSION to already carry an email. The configurator attaches
 * one at session-save time, but a session minted by `activate-item` (product activation / upgrade)
 * has none, and the FE can't attach one (`/save` → INVALID_SESSION_STATE, `/load` ignores it), so
 * this 500s `SESSION_NO_EMAIL` for that path. The Activate modal avoids the call entirely by using
 * the `provider_reference` `activate-item` returns INLINE; the "Finish payment" card path can't be
 * fixed FE-side until the backend associates the owner's email (it has the token) or derives it
 * from the Authorization header we now send.
 */
/** Owner Bearer header for the invoice endpoints, read from the store (empty when unauthenticated). */
function invoiceAuthHeaders(): Record<string, string> {
  const authToken = store.getState().authentication.access_token;
  return authToken ? { Authorization: authToken } : {};
}

/** Whether an `/invoice` error is the backend's "this voucher was already redeemed by this user"
 *  rejection (HTTP 500 with `error.code === 'VOUCHER_ALREADY_REDEEMED'`). The voucher is still
 *  applied to the session from its persisted `configuration_data.voucher_code`, so the caller can
 *  safely retry WITHOUT the explicit param rather than block checkout. */
function isVoucherAlreadyRedeemed(err: unknown): boolean {
  const data = (err as { response?: { data?: { error?: { code?: string } } } })?.response?.data;
  return data?.error?.code === 'VOUCHER_ALREADY_REDEEMED';
}

/**
 * Create (or re-fetch the idempotent pending) session invoice.
 *
 * When a `voucherCode` is passed it is sent as a top-level `voucher_code` body param so the backend
 * redeems it against this invoice (in addition to the code already persisted in the session's
 * `configuration_data`). If the code was already redeemed by this user, the endpoint 500s; since the
 * voucher is still applied from `configuration_data`, we transparently retry WITHOUT the param so
 * checkout isn't blocked.
 */
export async function createSessionInvoice(sessionId: string, voucherCode?: string | null): Promise<SessionInvoice> {
  const post = (body: Record<string, unknown>): Promise<{ data?: unknown }> =>
    axios.post(`${window.config.sessionApiUrl}/invoice`, body, { headers: invoiceAuthHeaders() });
  try {
    const res = await post(voucherCode ? { session_id: sessionId, voucher_code: voucherCode } : { session_id: sessionId });
    return (res.data ?? {}) as SessionInvoice;
  } catch (err) {
    if (voucherCode && isVoucherAlreadyRedeemed(err)) {
      const res = await post({ session_id: sessionId });
      return (res.data ?? {}) as SessionInvoice;
    }
    throw err;
  }
}

/** Whether an invoice is already fully settled: the case where a voucher covers the WHOLE bill, so
 *  the backend marks the invoice `paid` on creation and no Stripe payment is needed. Checkout then
 *  skips Stripe and jumps straight to the success screen. */
function isInvoicePaid(invoice: SessionInvoice): boolean {
  return invoice.status === 'paid';
}

/**
 * The result of preparing a session invoice for checkout: either it is already fully covered (a
 * voucher paid the whole bill, no Stripe step), or it needs a Stripe payment for the remaining
 * amount (carrying the PaymentIntent `clientSecret`).
 */
export type InvoiceOutcome = { paid: true } | { paid: false; clientSecret: string };

/** Create the session invoice and return only its Stripe clientSecret. Used by the legacy Spark
 *  checkout, which has no back-and-edit flow to guard against; the configurator checkouts go through
 *  {@link reconcileSessionInvoice} instead. */
export async function fetchInvoiceClientSecret(sessionId: string): Promise<string> {
  const { provider_reference } = await createSessionInvoice(sessionId);
  if (!provider_reference) throw new Error('invoice returned no provider_reference');
  return provider_reference;
}

/**
 * Void a still-open configurator invoice so the next {@link createSessionInvoice} mints a FRESH one
 * against the current selection instead of returning this (stale) one.
 *
 * `POST ${sessionApiUrl}/cancel-invoice { session_id, invoice_id }` (both uuid, auth required). This
 * is SESSION-scoped, deliberately NOT the wallet's user-scoped `${userApiUrl}/invoice/cancel`
 * (`cancelInvoice` thunk): that one keys on the owner `user_id` from the token and 500s Prisma
 * "record not found" for a session invoice whose owner association is absent. See
 * `docs/api.owners-hub.session.md`.
 *
 * NOTE: not yet deployed to test1 as of 2026-07-09 (the route 404s there); coded to the backend's
 * Joi contract — re-verify the success shape once it ships.
 */
export async function cancelSessionInvoice(sessionId: string, invoiceId: string): Promise<void> {
  await axios.post(
    `${window.config.sessionApiUrl}/cancel-invoice`,
    { session_id: sessionId, invoice_id: invoiceId },
    { headers: invoiceAuthHeaders() },
  );
}

/** Reissue-loop bounds. After a cancel, the backend can take up to ~10s to stop handing back the
 *  just-cancelled invoice from `POST /invoice` (the cancel propagates asynchronously), so we poll
 *  with a capped backoff over a generous budget before giving up. Only exercised on the (rare)
 *  stale-invoice path; the happy path never waits. */
const REISSUE_TIMEOUT_MS = 20000;
const REISSUE_MIN_DELAY_MS = 500;
const REISSUE_MAX_DELAY_MS = 1500;
const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

/** Opt-in tracing for the reconcile flow. Enable in the browser with `localStorage.debug = 'invoice'`
 *  to log each create / cancel / reissue step (id + status + price) — off (silent) by default. */
function invoiceDebug(...args: unknown[]): void {
  try {
    if ((localStorage.getItem('debug')?.indexOf('invoice') ?? -1) !== -1) {
      // eslint-disable-next-line no-console
      console.log('[invoice]', ...args);
    }
  } catch {
    /* localStorage unavailable — tracing simply stays off */
  }
}

const invoiceTag = (i: SessionInvoice): string => `${i?.id} (${i?.status}, ${i?.price}, secret=${!!i?.provider_reference})`;

/**
 * Create the session invoice and return a Stripe clientSecret GUARANTEED to match the live session.
 *
 * Because `POST /invoice` is idempotent per session, a buyer who reached Stripe, went Back, switched
 * campaign / edited the feature mix, then returned would otherwise be handed the ORIGINAL invoice and
 * charged its stale amount. So we compare the returned invoice's frozen snapshot against the current
 * `config` (bound campaign + selected items); on a mismatch we CANCEL it and RE-CREATE a fresh one —
 * which the backend snapshots against the now-current session — and return that.
 *
 * The reissue is retried until a genuinely new, chargeable invoice comes back — i.e. a DIFFERENT id
 * from the one just cancelled AND carrying a Stripe `provider_reference` — so a slow cancel/create or
 * a not-yet-attached PaymentIntent can't make us fall back to the stale invoice or a secretless one.
 *
 * The happy path (a still-valid invoice) costs exactly one request and never waits. Throws if no
 * fresh invoice / clientSecret is obtained — the caller surfaces a retry rather than charging a
 * stale amount. Trace it with `localStorage.debug = 'invoice'`.
 */
export async function reconcileSessionInvoice(
  sessionId: string,
  config: SessionInvoiceConfig | undefined | null,
  voucherCode?: string | null,
): Promise<InvoiceOutcome> {
  let invoice = await createSessionInvoice(sessionId, voucherCode);
  invoiceDebug('created', invoiceTag(invoice));

  // A voucher can cover the WHOLE bill, in which case the backend marks the invoice `paid` on
  // creation, so there is nothing left to charge: skip Stripe entirely. A paid invoice is
  // terminal (never stale), so short-circuit before the reissue logic.
  if (isInvoicePaid(invoice)) {
    invoiceDebug('fully covered by voucher, no Stripe', invoiceTag(invoice));
    return { paid: true };
  }

  if (invoice.id && !sessionInvoiceMatchesConfig(invoice, config)) {
    const staleId = invoice.id;
    invoiceDebug('stale vs live session → cancelling', staleId);
    // Cancel, but do NOT let a cancel error abort the reissue: the endpoint can reject (non-2xx /
    // shape) even when it voided the invoice server-side, and stopping here is exactly the reported
    // "invoice gets cancelled but no new one is issued" symptom. Try to reissue regardless — the
    // freshness check below is what decides whether the cancel really took.
    try {
      await cancelSessionInvoice(sessionId, staleId);
      invoiceDebug('cancelled', staleId);
    } catch (err) {
      invoiceDebug('cancel threw — reissuing anyway', String(err));
    }

    // Re-create against the now-current session, and keep polling until we actually get a FRESH,
    // chargeable invoice (a NEW id + a Stripe secret), OR a fresh invoice the voucher fully covers
    // (paid, no secret needed). The cancel propagates asynchronously: for up to ~10s `POST /invoice`
    // keeps returning the just-cancelled invoice (still `pending`, same id), so a reissue that
    // resolves to that row, or to an unpaid invoice whose PaymentIntent isn't attached yet, is
    // "not ready" and retried with a capped backoff over REISSUE_TIMEOUT_MS, never charged.
    invoice = await createSessionInvoice(sessionId, voucherCode);
    invoiceDebug('reissued', invoiceTag(invoice));
    const deadline = Date.now() + REISSUE_TIMEOUT_MS;
    let delay = REISSUE_MIN_DELAY_MS;
    while (
      invoice.id === staleId ||
      (!invoice.provider_reference && !isInvoicePaid(invoice))
    ) {
      if (Date.now() >= deadline) break;
      invoiceDebug('reissue not ready — waiting', delay);
      await sleep(delay);
      invoice = await createSessionInvoice(sessionId, voucherCode);
      invoiceDebug('reissued', invoiceTag(invoice));
      delay = Math.min(Math.round(delay * 1.5), REISSUE_MAX_DELAY_MS);
    }
    if (invoice.id === staleId) {
      throw new Error('invoice reissue kept returning the cancelled invoice');
    }
    if (isInvoicePaid(invoice)) {
      invoiceDebug('reissued invoice fully covered by voucher, no Stripe', invoiceTag(invoice));
      return { paid: true };
    }
  }

  const clientSecret = invoice.provider_reference;
  if (!clientSecret) throw new Error('invoice returned no provider_reference');
  return { paid: false, clientSecret };
}